import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import WeekHoursEditor from "@/components/settings/WeekHoursEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import RolesCard from "@/components/settings/RolesCard";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { CalendarClock, CalendarRange, Clock, FileStack, ListChecks, Tags, Users } from "lucide-react";
import DeadlinesCard from "@/components/settings/DeadlinesCard";
import StatusesCard from "@/components/settings/StatusesCard";
import Hours from "@/pages/Hours";
import Templates from "@/pages/Templates";
import Team from "@/pages/Team";

const field = "rounded-xl bg-[#FDFBF7] border-[#103080]/20 h-11";
const card = "rounded-2xl border border-[#103080]/10 bg-white/70 ambient p-6 sm:p-8";
const primary = "rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200";

function HoursCard() {
  const { user, setUser } = useAuth();
  const [s, setS] = useState(user.settings);
  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      const { data } = await api.put("/me/settings", { ...s, min_block: Number(s.min_block) });
      setUser({ ...user, settings: data });
      toast.success("Working hours saved. Your week will re-plan around them.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section className={card} data-testid="hours-card">
      <h2 className="font-serif-brand font-semibold text-2xl text-[#103080]">Working hours</h2>
      <p className="text-sm text-[#0F172A]/60 mt-1 mb-6">Your usual week. Each day can have its own hours and lunch. Tasks are only scheduled inside these hours (My hours can change individual weeks).</p>
      <WeekHoursEditor value={s} onChange={setS} testPrefix="settings" />
      <div className="mt-4 max-w-xs space-y-1.5">
        <Label>Shortest work block (minutes)</Label>
        <Input type="number" min={15} step={15} value={s.min_block} onChange={(e) => set("min_block")(e.target.value)} className={field} data-testid="settings-min-block" />
      </div>
      <button onClick={save} className={`${primary} mt-6`} data-testid="settings-save-hours-button">Save working hours</button>
    </section>
  );
}

function PasswordCard() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password changed");
      setF({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className={card} data-testid="password-card">
      <h2 className="font-serif-brand font-semibold text-2xl text-[#103080] mb-6">Change password</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Current password</Label>
          <Input type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={field} data-testid="current-password-input" />
        </div>
        <div className="space-y-1.5">
          <Label>New password (min 8)</Label>
          <Input type="password" required minLength={8} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={field} data-testid="new-password-input" />
        </div>
      </div>
      <button type="submit" className={`${primary} mt-6`} data-testid="change-password-button">Update password</button>
    </form>
  );
}

function DigestCard() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const on = user.digest_enabled !== false;
  const toggle = async (v) => {
    try {
      await api.put("/me/digest", { enabled: v });
      setUser({ ...user, digest_enabled: v });
      toast.success(v ? "Morning email turned on" : "Morning email turned off");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/digest/test");
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={card} data-testid="digest-card">
      <h2 className="font-serif-brand font-semibold text-2xl text-[#103080]">Morning email</h2>
      <p className="text-sm text-[#0F172A]/60 mt-1 mb-5">At 8am UK time, Monday to Friday, on days you work: today's planner tasks with times, today's meetings and job cards due this week.</p>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-3 text-sm text-[#103080]">
          <Switch checked={on} onCheckedChange={toggle} data-testid="digest-toggle" />{on ? "On" : "Off"}
        </label>
        <button onClick={test} disabled={busy} data-testid="digest-test-button"
          className="rounded-full border border-[#103080]/20 px-5 h-10 text-sm font-medium text-[#103080] hover:border-[#0090D0] transition-colors duration-200 disabled:opacity-60">{busy ? "Sending…" : "Send me a test email now"}</button>
      </div>
    </section>
  );
}

function General() {
  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-general">
      <HoursCard />
      <DigestCard />
      <PasswordCard />
    </div>
  );
}

const TABS = [
  { to: "", label: "Working week", icon: CalendarClock, testId: "settings-tab-general" },
  { to: "hours", label: "My hours", icon: Clock, testId: "settings-tab-hours" },
  { to: "roles", label: "Roles", icon: Tags, testId: "settings-tab-roles", admin: true },
  { to: "deadlines", label: "Deadlines", icon: CalendarRange, testId: "settings-tab-deadlines", admin: true },
  { to: "statuses", label: "Job statuses", icon: ListChecks, testId: "settings-tab-statuses", admin: true },
  { to: "templates", label: "Templates", icon: FileStack, testId: "settings-tab-templates", admin: true },
  { to: "team", label: "Team", icon: Users, testId: "settings-tab-team", admin: true },
];

export default function Settings() {
  const { user } = useAuth();
  return (
    <div className="space-y-8 fade-up" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">Settings</p>
        <h1 className="font-serif-brand font-semibold text-[#103080] text-4xl sm:text-5xl">Set up <i className="text-[#0090D0]">your hub</i></h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[210px_minmax(0,1fr)] gap-8">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto thin-scroll lg:sticky lg:top-24 h-fit">
          {TABS.filter((t) => !t.admin || user.role === "admin").map(({ to, label, icon: Icon, testId }) => (
            <NavLink key={testId} to={to ? `/settings/${to}` : "/settings"} end data-testid={testId}
              className={({ isActive }) => `inline-flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors duration-200 ${isActive ? "bg-[#103080] text-white" : "text-[#103080] hover:bg-[#103080]/5"}`}>
              <Icon size={16} />{label}
            </NavLink>
          ))}
        </nav>
        <div className="min-w-0">
          <Routes>
            <Route index element={<General />} />
            <Route path="hours" element={<Hours />} />
            <Route path="roles" element={user.role === "admin" ? <div className="max-w-4xl"><RolesCard /></div> : <Navigate to="/settings" replace />} />
            <Route path="deadlines" element={user.role === "admin" ? <DeadlinesCard /> : <Navigate to="/settings" replace />} />
            <Route path="statuses" element={user.role === "admin" ? <StatusesCard /> : <Navigate to="/settings" replace />} />
            <Route path="templates" element={user.role === "admin" ? <Templates /> : <Navigate to="/settings" replace />} />
            <Route path="team" element={user.role === "admin" ? <Team /> : <Navigate to="/settings" replace />} />
            <Route path="*" element={<Navigate to="/settings" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
